import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useApi } from "../../../../../hooks/useApi";
import DefaultModalLayout from "../../../../Modal/DefaultModalLayout";
import { ModalLabel } from "../../../Users/UserDetail/CommentModal/TargetContainer";
import { GetSubscriptionSetData_V3 } from "../../../../../ApiTS/ServiceDatasTS_V3";

interface SlotConfig {
  isOpen: boolean;
  isDirect: boolean;
}

interface RecruitmentSetModalProps {
  cancelEvent: () => void;
  partyId: string;
  maxmemb: number;
  members: number;
  subsId: number;
  invitememb: number;
  invitemembers: number;
  updateEvent: () => Promise<void>;
}

export default function RecruitmentSetModal({
  cancelEvent,
  partyId,
  maxmemb,
  members,
  subsId,
  invitememb,
  invitemembers,
  updateEvent,
}: RecruitmentSetModalProps) {
  const [submitClick, setSubmitClick] = useState(false);

  const subsData = GetSubscriptionSetData_V3(subsId);
  const recruitType = subsData?.RCR_TYP; // 모집 타입 (1: 직접 모집만)

  // 파티장 제외한 전체 슬롯 수
  const totalSlots = Math.max((subsData?.POL_SHARMX ?? 1) - 1, 0);
  // 현재 파티원(파티장 제외)이 차지한 슬롯 수
  const occupiedSlots = Math.max(members - 1, 0);
  // 남은 초대(직접 모집) 슬롯 수: 전체 초대 설정 - 이미 초대로 참여한 수
  const remainingInvite = Math.max(invitememb - invitemembers, 0);

  // 초기 슬롯 설정
  // 전체 슬롯(파티장 제외) 중 앞쪽 occupiedSlots개는 점유됨, 나머지가 설정 가능
  const buildInitialSlots = (slotCount: number): SlotConfig[] => {
    const openEmptySlots = Math.max(maxmemb - members, 0);
    return Array.from({ length: slotCount }, (_, i) => {
      const isOccupied = i < occupiedSlots;
      if (isOccupied) {
        return { isOpen: false, isDirect: false };
      }
      const emptyIndex = i - occupiedSlots;
      return {
        isOpen: emptyIndex < openEmptySlots,
        isDirect:
          emptyIndex < openEmptySlots &&
          emptyIndex < remainingInvite, // 웹 기준: 남은 초대 슬롯 수 이내면 직접 모집
      };
    });
  };

  const [slots, setSlots] = useState<SlotConfig[]>(() =>
    buildInitialSlots(totalSlots),
  );
  const [originSlots, setOriginSlots] = useState<SlotConfig[]>(() =>
    buildInitialSlots(totalSlots),
  );

  useEffect(() => {
    if (totalSlots > 0) {
      const initial = buildInitialSlots(totalSlots);
      setSlots(initial);
      setOriginSlots(initial);
    }
  }, [totalSlots]);

  // 슬롯 열기/닫기 토글
  const handleToggleOpen = (index: number) => {
    setSlots((prev) => {
      const editableOpenCount = prev
        .slice(occupiedSlots)
        .filter((s) => s.isOpen).length;
      const slot = prev[index];

      // 마지막 열린 슬롯을 닫으려 할 때 차단 (최소 1명 모집 필수)
      // if (slot.isOpen && editableOpenCount <= 1) {
      //   toast.error("파티원은 1명 이상 모집해야 해요");
      //   return prev;
      // }

      return prev.map((s, i) =>
        i === index
          ? {
              ...s,
              isOpen: !s.isOpen,
              isDirect: !s.isOpen
                ? recruitType === 1
                  ? true // recruitType이 1이면 항상 직접 모집
                  : originSlots[i]?.isDirect ?? false // 원래 직접 모집이었으면 복원
                : false, // 닫을 때 isDirect 초기화
            }
          : s,
      );
    });
  };

  // 슬롯 공개/직접 토글
  const handleToggleType = (index: number) => {
    setSlots((prev) =>
      prev.map((slot, i) =>
        i === index ? { ...slot, isDirect: !slot.isDirect } : slot,
      ),
    );
  };

  // 최종 계산 (점유 슬롯 제외, 설정 가능한 슬롯만)
  const editableSlots = slots.slice(occupiedSlots);
  const openSlotCount = editableSlots.filter((s) => s.isOpen).length;
  const directSlotCount = editableSlots.filter(
    (s) => s.isOpen && s.isDirect,
  ).length;
  const newMaxmemb = members + openSlotCount;
  const newInvitememb = invitemembers + directSlotCount; // 웹 기준: 기존 초대 참여 인원 + 새 직접 모집 슬롯 수

  const isChanged = newMaxmemb !== maxmemb || newInvitememb !== invitememb;

  const { response } = useApi({
    method: "post",
    url: submitClick ? `party/${partyId}/chmembers` : undefined,
    data: {
      maxmemb: newMaxmemb - 1,
      invitememb: newInvitememb,
    },
  });

  useEffect(() => {
    if (response?.status === 200) {
      cancelEvent();
      updateEvent();
      toast.success("모집인원이 설정되었습니다.", {});
    }
  }, [response]);

  return (
    <DefaultModalLayout
      modalTitle="모집인원 설정"
      isSmallModal={false}
      closeEvent={() => cancelEvent()}
      confirmEvent={() => setSubmitClick(true)}
      type="input"
      confirmDisabled={!isChanged}
    >
      <>
        <SummaryRow>
        <SummaryItem>
          <SummaryLabel>현재원(파티장 포함)</SummaryLabel>
          <SummaryValue>{members}명</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>모집원(파티장 포함)</SummaryLabel>
          <SummaryValue>{newMaxmemb}명</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>직접 모집 파티원</SummaryLabel>
          <SummaryValue>{newInvitememb}명</SummaryValue>
        </SummaryItem>
      </SummaryRow>

      <SlotHeaderRow>
        <SlotHeaderLabel style={{ flex: 1 }}>슬롯</SlotHeaderLabel>
        <SlotHeaderLabel style={{ width: 80, textAlign: "center" }}>
          열기/닫기
        </SlotHeaderLabel>
        <SlotHeaderLabel style={{ width: 80, textAlign: "center" }}>
          공개/직접
        </SlotHeaderLabel>
      </SlotHeaderRow>

      {slots.map((slot, index) => {
        const isOccupied = index < occupiedSlots;
        return (
          <SlotRow key={index}>
            <SlotLabel>
              슬롯 {index + 1}
              {isOccupied && <OccupiedBadge>참여중</OccupiedBadge>}
            </SlotLabel>
            {isOccupied ? (
              <>
                <DisabledLabel>-</DisabledLabel>
                <DisabledLabel>-</DisabledLabel>
              </>
            ) : (
              <>
                <ToggleBtn
                  isActive={slot.isOpen}
                  onClick={() => handleToggleOpen(index)}
                >
                  {slot.isOpen ? "열기" : "닫기"}
                </ToggleBtn>
                <ToggleBtn
                  isActive={slot.isDirect}
                  onClick={() => handleToggleType(index)}
                  disabled={!slot.isOpen}
                >
                  {slot.isDirect ? "직접" : "공개"}
                </ToggleBtn>
              </>
            )}
          </SlotRow>
        );
      })}
      </>
    </DefaultModalLayout>
  );
}

const SummaryRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-radius: 6px;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

const SummaryLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #858c97;
  margin-bottom: 4px;
`;

const SummaryValue = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: #1e1e1e;
`;

const SlotHeaderRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e5e5e5;
  margin-bottom: 4px;
`;

const SlotHeaderLabel = styled.span`
  font-size: 12px;
  font-weight: bold;
  color: #858c97;
`;

const SlotRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const SlotLabel = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #1e1e1e;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OccupiedBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  background-color: #a0a5ad;
  padding: 2px 6px;
  border-radius: 3px;
`;

const DisabledLabel = styled.span`
  width: 64px;
  height: 32px;
  margin: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #d0d0d0;
`;

interface SToggleBtn {
  isActive: boolean;
}

const ToggleBtn = styled.button<SToggleBtn>`
  width: 64px;
  height: 32px;
  margin: 0 8px;
  border-radius: 4px;
  border: 1px solid
    ${({ isActive }) => (isActive ? "#00c7c7" : "#d9d9d9")};
  background-color: ${({ isActive }) =>
    isActive ? "#e6fafa" : "#ffffff"};
  color: ${({ isActive }) => (isActive ? "#00c7c7" : "#858c97")};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    border-color: #00c7c7;
  }
`;
